import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { ID } from '../../../types/api/ID';
import { moveElementInList } from '../../DragAndDrop';
import { sendLiveRegionMessage } from '../../../components/atoms/LiveRegionGlobal/LiveRegionGlobal';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { SonForm } from '../../Form2';
import { AnyParameter } from '../../Form2/types/SonForm';

const byId = (id: string) => (item: ID) => item.id === id;
const findElementIndexById = <T extends ID>(items: T[], id: string) => items.findIndex(byId(id));
const getNextIdInList = <T extends ID>(items: T[], id: string) => {
  const currentIndex = findElementIndexById(items, id);
  return currentIndex !== -1 && items.length > currentIndex + 1 ? items[currentIndex + 1].id : undefined;
};

const getPreviousIdInList = <T extends ID>(items: T[], id: string) => {
  const currentIndex = findElementIndexById(items, id);
  return currentIndex > 0 ? items[currentIndex - 1].id : undefined;
};

export const useSortableList = <TItem extends { id: string }>(
  sortableItemsState: [TItem[], Dispatch<SetStateAction<TItem[]>>],
  form?: SonForm<AnyParameter>,
) => {
  const { t } = useSonTranslation('phrases');
  const [error, setError] = useState<{ error: AxiosError; id: string }>();
  const [sortableItems, setItems] = sortableItemsState;
  const [movedItemId, setMovedItemId] = useState<string | undefined>();
  const [previousItems, setPreviousItems] = useState<TItem[]>(sortableItems);

  const submit = useCallback(
    (itemId: string) => {
      setError(undefined);
      const paramName = form?.params?.[0].name;
      if (!form || !paramName || !form?.url) {
        return;
      }

      return axios
        .post(
          form.url,
          new URLSearchParams({
            [paramName]: sortableItems.map(item => item.id).join(','),
          }),
        )
        .catch((e: AxiosError) => {
          setError({ error: e, id: itemId });
          if (e.response?.data && Array.isArray(e.response?.data)) {
            setItems(
              e.response?.data.reduce((acc, id) => {
                const result = sortableItems.find(element => element.id === id);

                if (result) {
                  acc.push(result);
                }

                return acc;
              }, []),
            );
          }
        })
        .then(() => {
          // the most central place to trigger a live region message that keeps
          // the screen reader user aware of the current successful interaction.
          // BE AWARE: this triggers live region message as well in drag&drop,
          // although you might need that as a screen reader user.
          const movedIndex = findElementIndexById(sortableItems, itemId);
          const previousIndex = findElementIndexById(previousItems, itemId);
          const direction = movedIndex < previousIndex ? t('moveSortableElements.up') : t('moveSortableElements.down');
          const number = Math.abs(movedIndex - previousIndex);
          const position = number > 1 ? t('moveSortableElements.positions') : t('moveSortableElements.position');
          sendLiveRegionMessage(t('moveSortableElements.phrase', { number, direction, position }));

          // Update the previous state
          setPreviousItems(sortableItems);
        });
    },
    [form, sortableItems, setItems, previousItems, t],
  );

  /**
   * 1) Update sortableItemsRef.current when new elements are added to items list.
   *    If not done adding initially multiple files will lead to missing update. [SON-12630]
   * 2) We centralized submit method to make sure that submit is fired when position already changed. [SON-13944]
   * */
  useEffect(() => {
    if (movedItemId) {
      submit(movedItemId);
    }
    // we reset state for additional moving items with same item
    setMovedItemId(undefined);
  }, [sortableItems, movedItemId, submit]);

  /**
   * Moving the item.
   * dontSubmitWhileDragging is needed for drag&drop. While dragging, we don't want to submit changes.
   * We submit changes only on dragEnd event.
   * */
  const move = useCallback(
    (sourceId: string, targetId: string, dontSubmitWhileDragging: boolean = false) => {
      setItems(prev => moveElementInList(prev, sourceId, targetId));
      if (!dontSubmitWhileDragging) {
        setMovedItemId(sourceId);
      }
    },
    [setItems],
  );

  const getPrevItem = (id: string) => getPreviousIdInList(sortableItems, id);
  const getNextItem = (id: string) => getNextIdInList(sortableItems, id);
  const findCurrentItemIndex = (id: string) => findElementIndexById(sortableItems, id);

  return {
    sortableItems,
    move,
    findCurrentItemIndex: findCurrentItemIndex,
    getPrevItem,
    getNextItem,
    submit,
    error,
  };
};
